'use client';

import { motion } from 'framer-motion';
import { Container } from '@/components/ui/container';
import { MapPin, Building2, Code, BarChart3, Users } from 'lucide-react';
import Image from 'next/image';

export default function AboutPage() {
	return (
		<main className="flex flex-col gap-32 pb-32">
			{/* Hero Section */}
			<section className="relative pt-32">
				{/* JCW Icon Background */}
				<div className="absolute inset-0 w-full h-full overflow-hidden">
					<div className="absolute -right-1/4 top-[90%] -translate-y-1/2">
						<Image
							src="/images/logo/SVG/Icon/JCW_Logo_Updated_2026-09.svg"
							alt="JCW Icon"
							width={1200}
							height={1200}
							priority
							className="opacity-[0.08] rotate-12"
						/>
					</div>
				</div>
				<Container className="relative z-10">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="max-w-4xl mx-auto text-center"
					>
						<h1 className="text-4xl md:text-5xl font-bold mb-6 font-space-grotesk">
							Your Tech Partner for Small Business Growth
						</h1>
						<div className="space-y-6 text-xl">
							<p className="font-semibold">
								Hi, I&apos;m <span className="text-purple-500">Trent</span> — founder of{' '}
								<span className="text-purple-500 underline decoration-purple-500/30 underline-offset-4">
									JovianCloudWorks
								</span>
								.
							</p>
							<p className="text-muted-foreground">
								After 10 years working as a{' '}
								<span className="text-purple-500 font-medium">business analyst</span>,{' '}
								<span className="text-purple-500 font-medium">project manager</span>, and{' '}
								<span className="text-purple-500 font-medium">data specialist</span>, I realized that
								small businesses are often stuck between two extremes:
							</p>
							<motion.div
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, delay: 0.2 }}
								className="flex flex-col md:flex-row items-center gap-4 text-lg"
							>
								<motion.div className="flex-1 text-center">
									<p className="text-muted-foreground/80 italic">
										{"DIY solutions that don't scale".split(' ').map((word, i) => (
											<motion.span
												key={i}
												initial={{ opacity: 0, y: -20, filter: 'blur(10px)' }}
												animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
												transition={{
													duration: 0.5,
													delay: 0.4 + i * 0.1,
													ease: 'easeOut',
												}}
												className="inline-block mr-1"
											>
												{word}
											</motion.span>
										))}
									</p>
								</motion.div>
								<motion.div
									initial={{ scale: 0, opacity: 0 }}
									animate={{ scale: 1, opacity: 1 }}
									transition={{ duration: 0.5, delay: 1.2 }}
									className="h-px md:h-8 md:w-px bg-border w-1/2 md:mx-4"
								></motion.div>
								<motion.div className="flex-1 text-center">
									<p className="text-muted-foreground/80 italic">
										{'Enterprise solutions that break the bank'.split(' ').map((word, i) => (
											<motion.span
												key={i}
												initial={{ opacity: 0, y: -20, filter: 'blur(10px)' }}
												animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
												transition={{
													duration: 0.5,
													delay: 1.4 + i * 0.1,
													ease: 'easeOut',
												}}
												className="inline-block mr-1"
											>
												{word}
											</motion.span>
										))}
									</p>
								</motion.div>
							</motion.div>
							<p className="text-muted-foreground">
								That&apos;s where I come in.{' '}
								<span className="text-purple-500 font-semibold">JovianCloudWorks</span> was built to
								give small businesses access to real tech support — without the overhead.
							</p>
							<div className="flex items-center justify-center gap-2 text-muted-foreground/80">
								<MapPin className="w-5 h-5 text-purple-500" />
								<p className="text-base">
									Serving SMBs in Alhambra, Monterey Park, Pasadena, Altadena, San Gabriel, and San
									Marino
								</p>
							</div>
						</div>
					</motion.div>
				</Container>
			</section>

			{/* Why Choose Us Section */}
			<section className="relative">
				<Container>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="max-w-4xl mx-auto"
					>
						<h2 className="text-3xl font-bold mb-8 font-space-grotesk">
							What Makes Our Approach Different:
						</h2>
						<div className="space-y-6">
							<div className="flex gap-4">
								<Building2 className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
								<div>
									<h3 className="text-xl font-semibold mb-2">Local Business Understanding</h3>
									<div className="space-y-3">
										<p className="text-muted-foreground">
											As a local business owner myself, I understand the unique challenges and
											opportunities facing businesses in our community. This local perspective
											allows me to provide{' '}
											<span className="text-purple-500/90 font-medium">tailored solutions</span> that
											work specifically for your market.
										</p>
										<p className="text-muted-foreground">
											I take the time to understand your business goals, your customers, and your
											local competitive landscape to ensure technology solutions that give you a{' '}
											<span className="text-purple-500/90 font-medium">real advantage</span>.
										</p>
									</div>
								</div>
							</div>
							<div className="flex gap-4">
								<Code className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
								<div>
									<h3 className="text-xl font-semibold mb-2">Family-Run Development</h3>
									<div className="space-y-3">
										<p className="text-muted-foreground">
											As a family-run business, we handle the technical work ourselves —{' '}
											<span className="text-purple-500/90 font-medium">
												with personal attention to every detail
											</span>
											. This means{' '}
											<span className="text-purple-500/90 font-medium">faster response times</span>,
											better communication, and solutions that truly fit your needs.
										</p>
										<p className="text-muted-foreground">
											We build custom solutions that address your specific challenges:
										</p>
										<ul className="list-none space-y-1 pl-4 text-muted-foreground">
											<li>
												• <span className="text-purple-500/90 font-medium">Workflow automation</span>{' '}
												for your operations
											</li>
											<li>
												• <span className="text-purple-500/90 font-medium">Custom dashboards</span> for
												your business metrics
											</li>
											<li>
												• <span className="text-purple-500/90 font-medium">Internal tools</span>{' '}
												designed for your team
											</li>
										</ul>
										<p className="text-muted-foreground italic">
											All delivered with the personal attention only a family business can provide.
										</p>
									</div>
								</div>
							</div>
							<div className="flex gap-4">
								<BarChart3 className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
								<div>
									<h3 className="text-xl font-semibold mb-2">Data-Driven Insights</h3>
									<div className="space-y-3">
										<p className="text-muted-foreground">
											I help you turn your business data into{' '}
											<span className="text-purple-500/90 font-medium">actionable insights</span> that
											drive growth. By analyzing your operations, customer behavior, and market
											trends, I create{' '}
											<span className="text-purple-500/90 font-medium">
												custom analytics solutions{' '}
											</span>
											that help you make better business decisions.
										</p>
										<p className="text-muted-foreground">
											Whether you need to track inventory, analyze customer preferences, or forecast
											demand, I build the tools that give you a{' '}
											<span className="text-purple-500/90 font-medium">competitive edge</span> in your
											market.
										</p>
									</div>
								</div>
							</div>
							<div className="flex gap-4">
								<Users className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
								<div>
									<h3 className="text-xl font-semibold mb-2">Long-Term Partnership</h3>
									<div className="space-y-3">
										<p className="text-muted-foreground">
											I&apos;m not just a service provider — I&apos;m your{' '}
											<span className="text-purple-500/90 font-medium">technology partner</span>. I
											take a vested interest in your long-term success and provide ongoing support
											as your business grows.
										</p>
										<p className="text-muted-foreground">
											As your business evolves, I help you adapt your technology strategy to meet
											new challenges and opportunities, ensuring you always have the{' '}
											<span className="text-purple-500/90 font-medium">right tools</span> to compete
											effectively.
										</p>
										<p className="text-muted-foreground italic">
											The result? A technology partner who truly understands your business and is{' '}
											<span className="text-purple-500/90 font-medium">invested in your success</span>.
										</p>
									</div>
								</div>
							</div>
						</div>
					</motion.div>
				</Container>
			</section>
		</main>
	);
}
